// @flow

import React, { Fragment } from 'react'
import { withRouter } from 'react-router'
import { Route, Switch, Redirect } from 'react-router-dom'
import type { RouterHistory } from 'react-router'
import clientData from 'utils/client-data'
import _ from 'lodash'

import {
  AnimatedSwitchPagination,
  SimpleLayout,
  SessionTimer,
  MainComponent,
  ProfileDetails,
  SlugProfile,
  Grab,
  Taken,
  Success,
  Picture,
  SettingsContainer,
  MyProfileSettings,
  ProfileSettings,
  Username,
  TurnOffConfirmation,
  About,
  PersonalMessage,
  Location,
  CoverSelect,
  CoverPhoto,
  CoverPan,
  ProfileReview,
  LandingPage,
  CreateForExistingUser,
  Preference,
  GrabLandingPage,
} from './async-loading'

import ErrorScreen from './screens/error'

const overpanelViews = [
  'picture',
  'grab',
  'settings',
  'taken',
  'error',
  'success',
  'location',
  'cover',
  'cover/select',
  'cover/pan',
  'status',
  'confirm',
  'username',
  'about',
  'personalmessage',
  'review',
  'welcome',
  'profile',
  'create',
  'preference',
  'landing',
  'session-timeout',
]

const isProfileTreatment = _.get(clientData, 'isProfileTreatment', false)
const isEditHandleTreatment = _.get(clientData, 'isEditHandleTreatment', false)

type AppProps = {
  history: RouterHistory,
}

class App extends React.Component<AppProps> {
  render() {
    return (
      <div>
        <SessionTimer />

        <Switch>
          {/* ANIMATED ROUTING WITHIN OVERPANEL DESIGN */}
          <Route exact path={`/my/(${overpanelViews.join('|')})`}>
            <SimpleLayout>
              <AnimatedSwitchPagination>
                {/* GRAB */}
                <GrabRoute exact path="/my/grab" component={Grab} />
                <GrabRoute exact path="/my/success" component={Success} />
                <GrabRoute exact path="/my/picture" component={Picture} />
                <GrabRoute exact path="/my/taken" component={Taken} />
                <GrabRoute
                  exact
                  path="/my/landing"
                  component={GrabLandingPage}
                />
                <GrabRoute
                  exact
                  path="/my/create"
                  component={CreateForExistingUser}
                />

                {/* SETTINGS */}
                <SettingsRoute
                  exact
                  path="/my/welcome"
                  component={LandingPage}
                />
                <SettingsRoute
                  exact
                  path="/my/settings"
                  component={
                    isProfileTreatment ? ProfileSettings : MyProfileSettings
                  }
                />
                <SettingsRoute
                  exact
                  path="/my/confirm"
                  component={TurnOffConfirmation}
                />
                {(isProfileTreatment || isEditHandleTreatment) && (
                  <SettingsRoute
                    exact
                    path="/my/username"
                    component={Username}
                  />
                )}
                {isProfileTreatment ? (
                  <SettingsRoute exact path="/my/about" component={About} />
                ) : (
                  <SettingsRoute
                    exact
                    path="/my/personalmessage"
                    component={PersonalMessage}
                  />
                )}
                <SettingsRoute exact path="/my/location" component={Location} />
                {!isProfileTreatment && (
                  <SettingsRoute
                    exact
                    path="/my/cover"
                    component={CoverPhoto}
                  />
                )}
                {isProfileTreatment && (
                  <SettingsRoute
                    exact
                    path="/my/cover/select"
                    component={CoverSelect}
                  />
                )}
                {isProfileTreatment && (
                  <SettingsRoute
                    exact
                    path="/my/cover/pan"
                    component={CoverPan}
                  />
                )}
                {!isProfileTreatment && (
                  <SettingsRoute
                    exact
                    path="/my/review"
                    component={ProfileReview}
                  />
                )}
                <SettingsRoute
                  exact
                  path="/my/profile"
                  component={ProfileDetails}
                />
                {!isProfileTreatment && (
                  <SettingsRoute
                    exact
                    path="/my/preference"
                    component={Preference}
                  />
                )}

                {/* ERROR */}
                <Route exact path="/my/error" component={ErrorScreen} />

                {/* UNMATCHED CATCH-ALL ROUTES */}
                <Route path="/my">
                  <Redirect to="/my/profile" />
                </Route>
                <Route path="/grab">
                  <Redirect to="/my/grab" />
                </Route>
              </AnimatedSwitchPagination>
            </SimpleLayout>
          </Route>

          <Route
            exact
            path={'/:slug(\\w{2,20})/:amountInfo?'}
            render={props => (
              <MainComponent>
                <SlugProfile {...props} />
              </MainComponent>
            )}
          />
          <Redirect to="/" />
        </Switch>
      </div>
    )
  }
}

type ReactComponent = () => React.Element | React.Component

function SettingsRoute({
  component: Comp,
  ...props
}: {
  component: ReactComponent,
}) {
  return (
    <Route
      exact
      render={routerProps => (
        <Fragment>
          <SettingsContainer
            {...routerProps}
            body={<Comp {...routerProps} />}
            slugDetails={clientData.slugDetails}
            merchantCategoryCodesList={clientData.merchantCategoryCodesList}
            renderErrorScreen={<ErrorScreen />}
          />
        </Fragment>
      )}
      {...props}
    />
  )
}

function GrabRoute({ component: Comp }: { component: ReactComponent }) {
  return (
    <Route
      exact
      render={routerProps => (
        <Comp
          {...routerProps}
          merchantCategoryCodesList={clientData.merchantCategoryCodesList}
        />
      )}
    />
  )
}

export default withRouter(App)
